import React, { useState, useRef, useCallback, useEffect } from "react";
import { X, ArrowLeft, Upload, Loader, Plus, PenLine, GraduationCap, Sparkles, Play } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiService } from "../apiService";
import { renderLatex } from "../study/renderLatex";
import { createHomeworkSession, updateHomeworkSession } from "../homework/homeworkApi";
import { createLectureSession, updateLectureSession, extractLectureTopics } from "../lecture/lectureApi";
import { busyMessageFor, getDefaultLanguage } from "../utils/constants";
import { useT } from "../i18n/useT";
import { useFocusTrap } from "../hooks/useFocusTrap";
import SubscriptionModal from "../components/SubscriptionModal";

const MAX_SELF_STUDY_SLOTS = 10;

// initialType/initialTopic let another surface (the Workspace composer) drop you straight
// into a flow with the topic you already typed, instead of re-asking for both.
// initialFiles does the same for an upload: Home's composer has an attach button, so the
// file is already chosen by the time this opens. Without a type it waits at the type step
// and extracts as soon as one is picked — the file is not enough to know whether a PDF is
// a homework set or a lecture.
/* `docked` renders the same flow as an extension of the Workspace composer instead of a
   centred dialog: same column, same width, same off-white surface and black edge, anchored
   to the same bottom offset, growing upward out of where the bar was. Only the shell
   changes — every step inside is identical, so the two callers cannot drift. */
const CreateProjectModal = ({ onClose, onCreated, generationQueue, initialType = null, initialTopic = "", initialFiles = null, uploadOnly = false, docked = false }) => {
  const { user, userData } = useAuth();
  const t = useT();

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Flow state
  const [step, setStep] = useState(initialType ? "create" : "type"); // "type" | "create"
  const [projectType, setProjectType] = useState(initialType); // "hw" | "lecture" | "self_study"
  // Where the seeded text lands depends on the flow: a Project starts from topics, so it
  // seeds the first slot (below); HW and Lecture start from an upload, so the only thing
  // the text can be is the title.
  const [title, setTitle] = useState(
    initialType && initialType !== "self_study" ? initialTopic : "",
  );
  const [creating, setCreating] = useState(false);

  // Language: from user settings, falling back to country detection, then "en"
  const language = userData?.preferences?.language || getDefaultLanguage(userData?.country_code);

  // HW / Lecture file upload
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState("upload"); // "upload" | "analyzing" | "select"
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [extractedItems, setExtractedItems] = useState([]); // problems (hw) or topics (lecture)
  const [selectedItems, setSelectedItems] = useState(new Set());
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  // Self Study slots
  const [videoSlots, setVideoSlots] = useState(initialTopic ? [initialTopic] : [""]);

  const handleSelectType = (type) => {
    // No subscriber check. Reading a document is metered by a daily quota on the server
    // (settings.PDF_EXTRACTS_PER_DAY, free tier included) rather than sold — putting the
    // paywall on the step that demonstrates the product meant a first-time user uploaded
    // their homework and got an upgrade modal.
    setProjectType(type);
    setStep("create");
  };

  const handleBack = () => {
    setStep("type");
    setProjectType(null);
    setTitle("");
    setFiles([]);
    setPhase("upload");
    setError(null);
    setExtractedItems([]);
    setSelectedItems(new Set());
    setVideoSlots([""]);
  };

  // ---- File handling (HW & Lecture) ----

  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const fileArray = Array.from(fileList).filter((f) => f && f.size > 0);
    if (fileArray.length === 0) return;

    // Warn about very large files (>50MB backend limit)
    const oversized = fileArray.find((f) => f.size > 50 * 1024 * 1024);
    if (oversized) {
      setError(`File "${oversized.name}" is too large (${Math.round(oversized.size / 1024 / 1024)}MB). Maximum is 50MB.`);
      return;
    }

    // Remember previous phase so we can restore on error when appending.
    // THE RESET LIVES IN A `finally`, NOT AT THE END OF THE CATCH. It used to be the catch's
    // last statement, so a throw inside the error handling itself — a missing import in one
    // branch was enough — skipped it and left phase stuck on "analyzing". That phase disables
    // the close button, the overlay click AND the Escape key, so the dialog became impossible
    // to dismiss without reloading the page. Nothing that runs below can strand it now.
    const wasSelecting = phase === "select";
    let nextPhase = wasSelecting ? "select" : "upload";
    setFiles((prev) => [...prev, ...fileArray]);
    setPhase("analyzing");
    setError(null);

    try {
      if (projectType === "hw") {
        const newProblems = [];
        let extractedTitle = "";
        for (const file of fileArray) {
          const fd = new FormData();
          fd.append("pdf", file);
          if (user?.uid) fd.append("user_id", user.uid);
          // So an abandoned or empty upload can still be attributed to the kind of project
          // the user was trying to make — no project doc exists yet at this point.
          fd.append("project_type", projectType);
          const data = await apiService.extractPdfQuestions(fd);
          if (data.questions?.length > 0) {
            newProblems.push(...data.questions.map((q) => q.text));
          }
          // First file to return one wins — on a multi-file drop the later documents are
          // usually more of the same assignment, not a different one.
          if (!extractedTitle && data.title) extractedTitle = data.title;
        }
        if (newProblems.length === 0 && extractedItems.length === 0) {
          setError(t('create_no_problems'));
          return;
        }
        // The extraction model already read every page, so it names the project: "Vector
        // Calculus Problem Set" instead of "scan_20260910_final(2)". Filename stays as the
        // fallback for the case where the model skips the TITLE line, which the backend
        // splitter tolerates on purpose. Anything the user typed themselves always wins.
        if (!title) {
          const aiTitle = extractedTitle || fileArray[0]?.name?.replace(/\.(pdf|png|jpg|jpeg|heic)$/i, "");
          setTitle(aiTitle || "Homework");
        }
        setExtractedItems((prev) => {
          const merged = [...prev, ...newProblems];
          // Select all new items by default
          setSelectedItems((prevSel) => {
            const next = new Set(prevSel);
            for (let i = prev.length; i < merged.length; i++) next.add(i);
            return next;
          });
          return merged;
        });
      } else {
        // Lecture
        const newTopics = [];
        let firstName = "";
        for (const file of fileArray) {
          const fd = new FormData();
          fd.append("pdf", file);
          if (user?.uid) fd.append("user_id", user.uid);
          // extract-topics serves lecture AND self-study; only the client knows which.
          fd.append("project_type", projectType);
          const data = await extractLectureTopics(fd);
          if (data.topics?.length > 0) {
            newTopics.push(...data.topics);
          }
          if (!firstName) {
            firstName = data.source_filename || file?.name?.replace(/\.(pdf|png|jpg|jpeg|heic)$/i, "") || "Lecture";
          }
        }
        if (newTopics.length === 0 && extractedItems.length === 0) {
          setError(t('create_no_topics'));
          return;
        }
        if (!title) setTitle(firstName);
        setExtractedItems((prev) => {
          const offset = prev.length;
          const withIds = newTopics.map((t, i) => ({ ...t, id: `t${offset + i}` }));
          const merged = [...prev, ...withIds];
          setSelectedItems((prevSel) => {
            const next = new Set(prevSel);
            for (let i = offset; i < merged.length; i++) next.add(i);
            return next;
          });
          return merged;
        });
      }
      nextPhase = "select";
    } catch (err) {
      if (err?.status === 503) {
        // Busy, gated, or down — busyMessageFor picks the wording from the error CODE
        // so the backend's own text never reaches a user.
        setError(busyMessageFor(err));
      } else if (err?.status === 429) {
        // OUT FOR TODAY, NOT LOCKED. Reading documents has a daily quota now
        // (settings.PDF_EXTRACTS_PER_DAY), and 429 is what it returns when spent —
        // answering that with "Unlock more with Student" sells a subscription that would
        // not lift the limit, which is both confusing and untrue.
        setError(t('create_extract_daily_limit'));
      } else if ([402, 403].includes(err?.status)) {
        setShowSubscriptionModal(true);
      } else {
        setError(err?.message || t('create_error'));
      }
    } finally {
      setPhase(nextPhase);
    }
  }, [projectType, user, title, t, phase, extractedItems.length]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  // A file handed in by the caller is extracted once, as soon as there is a type to extract
  // it AS. Held in a ref rather than state so a re-render cannot run it twice, and skipped
  // for self_study, which has no upload step at all — handleFiles treats anything that is
  // not "hw" as a lecture, so reaching it with self_study would silently call the wrong API.
  const pendingFilesRef = useRef(initialFiles);
  useEffect(() => {
    if (!pendingFilesRef.current) return;
    if (projectType !== "hw" && projectType !== "lecture") return;
    const files = pendingFilesRef.current;
    pendingFilesRef.current = null;
    handleFiles(files);
  }, [projectType, handleFiles]);

  const toggleItem = (idx) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // ---- Self Study slot management ----

  const slotsScrollRef = useRef(null);

  const addSlot = () => {
    if (videoSlots.length < MAX_SELF_STUDY_SLOTS) {
      const nextIdx = videoSlots.length;
      setVideoSlots((prev) => [...prev, ""]);
      // Focus the row you just asked for. Without this, "Add another video" gave you an
      // empty box you then had to go and click, which is most of what made it feel clunky.
      requestAnimationFrame(() => {
        const el = document.getElementById(`topic-slot-${nextIdx}`);
        if (!el) return;
        el.focus();
        el.scrollIntoView({ block: "nearest" });
      });
    }
  };

  const removeSlot = (idx) => {
    if (videoSlots.length > 1) {
      setVideoSlots((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const updateSlot = (idx, value) => {
    setVideoSlots((prev) => prev.map((s, i) => (i === idx ? value : s)));
  };

  // ---- Create project ----

  const canCreate = () => {
    if (!title.trim()) return false;
    if (projectType === "video") return true;
    if (projectType === "self_study") {
      return videoSlots.some((s) => s.trim());
    }
    return phase === "select" && selectedItems.size > 0;
  };

  // Kick off generation for a list of items.
  // Returns { videoJobs, serverBusy, needsUpgrade } — never sets modal state, so
  // it can run in the background after the modal has closed.
  // items: [{ key, text, title }] — text is the API prompt, title is the clean display name
  const startGenerationForItems = async (items) => {
    const uid = String(user?.uid || "");
    const results = await Promise.allSettled(
      items.map(({ text }) =>
        apiService.startUnifiedGeneration({ text, user_id: uid, model: "mode_fast_37", language })
      )
    );
    const serverBusy = results.some(r => r.status === "rejected" && r.reason?.status === 503);
    const needsUpgrade = results.some(r => r.status === "rejected" && [402, 403, 429].includes(r.reason?.status));

    const videoJobs = {};
    for (let i = 0; i < items.length; i++) {
      const { key } = items[i];
      const r = results[i];
      if (r.status === "fulfilled" && r.value?.job_id) {
        videoJobs[key] = { job_id: r.value.job_id, status: "generating", url: null };
      } else {
        console.error(`[Projects] Generation failed for ${key}:`, r.reason || r.value);
        videoJobs[key] = { status: "failed" };
      }
    }
    return { videoJobs, serverBusy, needsUpgrade };
  };

  // Client-side credit gate (mirrors HomePage): 1 credit per item for flash videos.
  // Returns true (and shows the upsell) when the user can't afford the request.
  const blockedByCredits = (itemCount) => {
    if (!user) return false;
    const remaining = (userData?.video_limit_this_month ?? 0) - (userData?.video_spend_this_month ?? 0);
    if (remaining < itemCount) {
      setShowSubscriptionModal(true);
      return true;
    }
    return false;
  };

  const handleCreate = () => {
    if (creating || !canCreate()) return;
    setError(null);

    // Build a plan for the selected type: the items to generate, how to create
    // the session, and how to persist the result. Everything after enqueue runs
    // in the background so the queue card appears instantly (like the Home page).
    let plan;
    if (projectType === "video") {
      const topic = { id: "t0", title: title.trim(), description: "" };
      plan = {
        kind: "video",
        items: [{ key: "t0", text: title.trim(), title: title.trim() }],
        createSession: () => createLectureSession(title.trim(), [topic], { t0: { status: "generating" } }),
        persist: (session, videoJobs) => {
          updateLectureSession(session.session_id, { project_type: "video" }).catch(() => {});
          updateLectureSession(session.session_id, { video_jobs: videoJobs }).catch(() => {});
          onCreated({ ...session, video_jobs: videoJobs, project_type: "video" }, "video");
        },
      };
    } else if (projectType === "hw") {
      const selectedTexts = extractedItems.filter((_, i) => selectedItems.has(i));
      const problemsData = selectedTexts.map((text, i) => ({
        text, difficulty: 2, topic: `Problem ${i + 1}`, estimated_minutes: 3,
      }));
      const placeholderJobs = Object.fromEntries(selectedTexts.map((_, i) => [`p${i}`, { status: "generating" }]));
      plan = {
        kind: "project",
        projectType: "hw",
        collection: "homework",
        items: selectedTexts.map((text, i) => ({ key: `p${i}`, text: text.slice(0, 2000), title: `Problem ${i + 1}` })),
        createSession: () => createHomeworkSession(title.trim(), problemsData, placeholderJobs),
        persist: (session, videoJobs) => {
          updateHomeworkSession(session.session_id, { video_jobs: videoJobs }).catch(() => {});
          onCreated({ ...session, video_jobs: videoJobs }, "hw");
        },
      };
    } else if (projectType === "lecture") {
      const selectedTopics = extractedItems.filter((_, i) => selectedItems.has(i));
      const placeholderJobs = Object.fromEntries(selectedTopics.map((t) => [t.id, { status: "generating" }]));
      plan = {
        kind: "project",
        projectType: "lecture",
        collection: "lecture",
        items: selectedTopics.map((t) => ({
          key: t.id,
          text: t.description ? `${t.title}: ${t.description}` : t.title,
          title: t.title,
        })),
        createSession: () => createLectureSession(title.trim(), selectedTopics, placeholderJobs),
        persist: (session, videoJobs) => {
          updateLectureSession(session.session_id, { video_jobs: videoJobs }).catch(() => {});
          onCreated({ ...session, video_jobs: videoJobs }, "lecture");
        },
      };
    } else {
      // Self Study
      const filledSlots = videoSlots.filter((s) => s.trim());
      const topics = filledSlots.map((slot, i) => ({ id: `t${i}`, title: slot.trim(), description: "" }));
      const placeholderJobs = Object.fromEntries(topics.map((t) => [t.id, { status: "generating" }]));
      plan = {
        kind: "project",
        projectType: "self_study",
        collection: "lecture",
        items: topics.map((t) => ({
          key: t.id,
          text: `Create an educational video about: ${t.title}`,
          title: t.title,
        })),
        createSession: () => createLectureSession(title.trim(), topics, placeholderJobs),
        persist: (session, videoJobs) => {
          updateLectureSession(session.session_id, { project_type: "self_study" }).catch(() => {});
          updateLectureSession(session.session_id, { video_jobs: videoJobs }).catch(() => {});
          onCreated({ ...session, video_jobs: videoJobs, project_type: "self_study" }, "self_study");
        },
      };
    }

    // Credit gate before anything is enqueued — keeps the modal open on failure.
    if (blockedByCredits(plan.items.length)) return;

    const projectTitle = title.trim();
    const tempId = `temp-${plan.kind}-${Date.now()}`;

    // 1) Optimistically add the queue card and close the modal immediately.
    if (plan.kind === "video") {
      generationQueue?.addJob({
        jobId: tempId,
        type: "video",
        source: "study",
        title: projectTitle,
        metadata: { credits: 1 },
      });
    } else {
      generationQueue?.addJob({
        jobId: tempId,
        type: "project",
        title: projectTitle,
        totalCount: plan.items.length,
        metadata: { projectType: plan.projectType, collection: plan.collection, totalItems: plan.items.length },
      });
    }
    onClose();

    // 2) Do the real work in the background, reconciling the temp job → real ids.
    (async () => {
      try {
        const session = await plan.createSession();
        /* THE COVER CAN OPEN AS SOON AS THE PROJECT EXISTS. The row is written here, but the
           queue card only learned its id after every generation request below had returned —
           so the card for the thing you just made spent its first seconds with nowhere to go.
           Told now, separately from the sub-job ids, which genuinely are not known yet. */
        if (plan.kind !== "video") {
          generationQueue?.updateProjectJob(tempId, { sessionId: session.session_id });
        }
        const { videoJobs, serverBusy, needsUpgrade } = await startGenerationForItems(plan.items);
        const jobIds = Object.values(videoJobs).filter((j) => j.job_id).map((j) => j.job_id);

        if (serverBusy || needsUpgrade || jobIds.length === 0) {
          generationQueue?.removeJob(tempId);
          return;
        }

        plan.persist(session, videoJobs);

        if (plan.kind === "video") {
          generationQueue?.updateJobId(tempId, jobIds[0]);
        } else {
          generationQueue?.updateProjectJob(tempId, { subJobIds: jobIds, sessionId: session.session_id });
        }
      } catch (err) {
        console.error("[Projects] Background generation failed:", err);
        generationQueue?.removeJob(tempId);
      }
    })();
  };

  // ---- Render ----

  /* WHAT ACTUALLY REACHES THIS STEP, as of now: a bare DROP, and nothing else. Every named
     route passes `initialType` and lands straight on the form — Video, Homework PDF, Lecture
     PDF and Project in the composer's mode picker all do, and since the PDF entry was split
     back into those two (see composerConfig.js) there is no longer a menu item that arrives
     here. What is left is dropping a file on the bar, or the legacy `?mode=pdf` URL: in both
     the file has said nothing about what it is, so the question is genuinely unanswered and
     this screen is the right place to ask it.

     The chooser is therefore always the two file types in practice; the other two stay in the
     list because a caller that opens the modal cold would otherwise get an empty screen, which
     is a worse failure than a dead array entry.

     When a file arrived with the request, only the types that can READ one are offered.
     Attaching a PDF on Home and then being asked to pick between four things — two of which
     (a typed video prompt, a topic list) cannot consume the file you just handed over — is
     the step that reads as being in the way. Homework and Lecture use different extractors
     (`extract-questions` vs topic extraction) and a PDF alone cannot say which it is, so the
     question is real; it is only the irrelevant half of it that goes. */
  const ALL_TYPE_OPTIONS = [
    { type: "video", icon: Play, label: t('create_video'), subtitle: t('create_video_sub'), color: "rose" },
    { type: "hw", icon: PenLine, label: t('create_homework'), color: "indigo" },
    { type: "lecture", icon: GraduationCap, label: t('create_lecture'), color: "purple" },
    { type: "self_study", icon: Sparkles, label: t('create_self_study'), subtitle: t('create_self_study_sub'), color: "teal" },
  ];
  const FILE_READING_TYPES = new Set(["hw", "lecture"]);
  // `uploadOnly` as well as a present file: the caller may know this is a document flow even
  // when the file itself is gone (a reload of /workspace?mode=upload — see pendingUpload.js).
  // Filtering on the file alone meant that reload showed all four types, including two that
  // cannot read a document.
  const typeOptions = (initialFiles?.length || uploadOnly)
    ? ALL_TYPE_OPTIONS.filter((o) => FILE_READING_TYPES.has(o.type))
    : ALL_TYPE_OPTIONS;

  const colorMap = {
    rose: { border: "border-rose-400", bg: "bg-rose-50/50", text: "text-rose-500" },
    indigo: { border: "border-indigo-400", bg: "bg-indigo-50/50", text: "text-indigo-500" },
    purple: { border: "border-purple-400", bg: "bg-purple-50/50", text: "text-purple-500" },
    teal: { border: "border-teal-400", bg: "bg-teal-50/50", text: "text-teal-500" },
  };

  const trapRef = useFocusTrap(true);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && phase !== 'analyzing') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, phase]);

  return (
    <>
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-center items-center"
      // Centred in BOTH modes now. Docked used to sit on the bottom edge so it read as the
      // composer growing upward, but these are blocking steps — an extraction wait and a
      // selection list — and a panel pinned to the floor reads as a toast you can ignore.
      // The left inset stays so it still lines up with the content column horizontally.
      style={docked ? { paddingLeft: "var(--magi-sidebar, 0px)" } : undefined}
      onClick={phase === "analyzing" ? undefined : onClose}
    >
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-modal-title"
        /* 720, DOWN FROM 880. The 880 was inherited from the Workspace composer, back when
           this panel grew upward out of that bar and had to be exactly as wide as it. It is
           centred in both modes now (see the overlay comment above), so matching the composer
           bought nothing and cost the shape: at 880 over this much content the dialog was
           roughly 2.4:1, which reads as a letterbox strip rather than a dialog.
           It then went to 720, and back out to 800 once the drop zone gained a left-aligned
           stack and artwork on the right. Width was never the defect — an EMPTY wide frame was.
           At 800 with the zone actually occupied this lands near 2:1, which is the reference
           layout's own ratio. */
        /* 900/max-w-3xl, up from 800/max-w-2xl. The list is the content now, the problem text
           is set at 18px, and several statements carry inline LaTeX that cannot be hyphenated —
           at the old width the longer ones wrapped to three lines and ran into the 4-line clamp.
           The extra ~100px buys a line back on most of them. */
        className={docked
          ? "ws-dock-panel bg-white border-2 border-black rounded-3xl w-full max-w-[900px] mx-6 max-h-[88dvh] flex flex-col overflow-hidden"
          : "bg-white rounded-3xl shadow-2xl w-full max-w-3xl mx-4 max-h-[88dvh] flex flex-col"}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 flex-shrink-0 border-b ${
          docked ? "border-black/10" : "border-gray-100"
        }`}>
          <div className="flex items-center gap-3">
            {/* Back only exists if there is somewhere to go back TO. When the caller hands in
                a type, the chooser was never a step you passed through — offering Back
                invented a "New Project" screen behind you and asked you to pick Homework a
                second time, having already picked it. Every current caller seeds a type, so
                in practice this arrow is now never shown; it stays for a caller that opens
                the modal cold. */}
            {step === "create" && phase !== "analyzing" && !initialType && (
              <button onClick={handleBack} aria-label="Back" className="p-1 text-slate-400 hover:text-slate-700 rounded transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h2 id="create-modal-title" className="text-[22px] font-extrabold text-slate-900 tracking-tight">
              {step === "type"
                ? t('create_new_project')
                : phase === "analyzing"
                  ? t('create_extracting')
                  : phase === "select"
                    ? projectType === "hw" ? t('create_select_problems') : t('create_select_topics')
                    : projectType === "video"
                      ? t('create_new_video')
                      : projectType === "hw"
                        ? t('create_new_hw')
                        : projectType === "lecture"
                          ? t('create_new_lecture')
                          : t('create_new_self_study')}
            </h2>
          </div>
          {phase !== "analyzing" && (
            <button onClick={onClose} aria-label="Close" className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Body */}
        {/* THE PANEL MUST NOT RESIZE BETWEEN STEPS. Dropping a file takes you from upload to
            analyzing, and analyzing renders neither the title field nor the 260px drop zone —
            just a line of status text and a progress bar. The body therefore collapsed by
            roughly a third the instant you dropped, and because the dialog is vertically
            centred it did not merely shrink, it jumped upward at the same time.
            THE FLOOR IS DELIBERATELY GENEROUS AND THE ZONE ABSORBS THE SLACK. Deriving the
            floor by adding up padding, input height and zone height was the first attempt, and
            it is the kind of arithmetic that is wrong by fifteen pixels and silently brings the
            jump back. Instead the upload step lays out as a column with the drop zone flex-1,
            so the zone stretches to eat whatever room the floor leaves over — no gap under it,
            and no dependence on the number being exactly right. Analyzing centres inside the
            same floor.
            hw/lecture only: video and self_study never pass through analyzing, and a floor
            there would just pad a short form. */}
        <div className={`flex-1 overflow-y-auto px-6 py-5 ${
          step === "create" && (projectType === "hw" || projectType === "lecture")
            ? "min-h-[380px]" : ""
        } ${phase === "upload" || phase === "analyzing" ? "flex flex-col" : ""}`}>
          {/* Step 1: Type selector */}
          {step === "type" && (
            <div className="grid grid-cols-2 gap-3">
              {typeOptions.map((opt) => {
                const Icon = opt.icon;
                const colors = colorMap[opt.color];
                return (
                  <button
                    key={opt.type}
                    onClick={() => handleSelectType(opt.type)}
                    className={`rounded-xl border-2 border-gray-200 hover:${colors.border} transition-all overflow-hidden text-center`}
                  >
                    {/* ONE DRAWN SCENE EACH, STILL — not a loop.
                     *
                     *  The tiles carried two autoplaying 0.3 MB clips (`home_cards_veo.py`,
                        variants tile_hw_a and tile_lec_b) which were themselves the fifth
                        answer to this tile; the four before them were rejected and the commit
                        that split Homework and Lecture back into their own menu entries read
                        that as evidence the screen had nothing to add. These are the sixth,
                        and the thing that changed is that they are SCENES rather than
                        diagrams: a worksheet on a desk with a pencil, and a lecture room with
                        derivatives on the board. A picture of a place is recognised before a
                        label is read, which is the only job this screen has left.
                     *
                     *  WHY A STILL. Motion was bought to stop the tile reading as clipart, and
                        a drawn scene does that on its own. Two clips decoding over a modal
                        that is already open is 0.25 MB and two decoders for something nobody
                        watches — the eye is here to answer a question and leave.
                     *
                     *  CROPPED TO 16:10 AT EXPORT, not by `object-cover`. The sources are 16:9,
                        so something had to go; taking the centre 1506px of width loses margin
                        at the sides and keeps every vertical pixel — the board, the teacher,
                        the whole sheet. Letting the browser crop would have taken it off the
                        top and bottom instead, which is where the content is. 800x500, so the
                        ~354px tile is still sharp at 2x.
                     *
                     *  THE LUCIDE ICON STAYS BEHIND as the fallback: if a file 404s the tile
                        must still say what it is. */}
                    <span className="block relative w-full aspect-[16/10] bg-gray-50">
                      <img
                        src={`/project-art/${opt.type}.jpg`}
                        alt=""
                        loading="eager"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                      <Icon className={`w-8 h-8 ${colors.text} absolute inset-0 m-auto -z-10`} />
                    </span>
                    {/* NO SUBTITLE. "Upload homework" under "Homework" and "Upload slides"
                        under "Lecture" restated the label with a verb in front of it, on a
                        screen reached by pressing New Project — the upload is not news. The
                        strings stay in the locale files: they are the UPLOAD BUTTON's label on
                        the next step, where the verb is the point. */}
                    <span className="block px-3 py-2.5 text-sm font-semibold text-gray-800">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2: Create form */}
          {step === "create" && (
            <>
              {/* Title input — VIDEO AND SELF-STUDY ONLY. An upload names itself: the extraction
                  model reads every page and returns a title, with the filename behind it as a
                  fallback, so asking for one first was asking for something about to be
                  overwritten — and it was the first thing on the screen, in front of the one
                  action the step actually has. The name is still editable, on the select step,
                  where it is filled in and there is something to name.
                  hw/lecture no longer render this on EITHER upload or analyzing, so the two
                  steps still match and the panel does not jump the instant you drop a file. */}
              {(projectType === "self_study" || projectType === "video") && (
                <div className="mb-5">
                  <label htmlFor="project-title" className="sr-only">{projectType === "video" ? t('create_video_prompt') : t('create_project_title')}</label>
                  <input
                    id="project-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={projectType === "video" ? t('create_video_prompt') : t('create_project_title')}
                    className={`w-full bg-transparent focus:outline-none transition-colors ${
                      docked
                        ? "text-[19px] font-semibold text-gray-900 placeholder-slate-600 placeholder:font-medium border-b border-black/15 pb-3 focus:border-black/50"
                        : "text-[19px] font-semibold text-gray-900 placeholder-slate-600 placeholder:font-medium border-b border-gray-200 pb-2.5 focus:border-indigo-400"
                    }`}
                    autoFocus
                    aria-describedby={error ? "create-error" : undefined}
                  />
                  {projectType === "video" && (
                    <p className="text-[13px] text-gray-400 mt-2">{t('create_video_hint')}</p>
                  )}
                </div>
              )}

              {/* HW / Lecture: File upload flow */}
              {(projectType === "hw" || projectType === "lecture") && (
                <>
                  {phase === "upload" && (
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={projectType === "hw" ? t('create_drop_hw') : t('create_drop_lecture')}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
                      /* THE ADOBE SHAPE: left-aligned stack, artwork holding the right.
                         Three centred versions failed before this one — grey and hollow, then a
                         pale lavender wash, then a 2.4:1 letterbox — and a sweep of seven live
                         PDF tools explained why: NONE of them centre one small thing in a large
                         frame. Every wide zone either fills its corners with real content, or
                         drops centring and puts art opposite the copy. This is the second.
                         Width is no longer the enemy once the frame is actually occupied, which
                         is why the panel is allowed back out to 800px above.
                         Hairline DASHED, 1px — the reference's border, not the 2px rule we had.
                         At this weight it reads as a boundary rather than as an unstyled default,
                         and it only works because the space inside it is full.
                         Warm neutral ground, because the reference's warmth comes from a red page
                         gradient behind a white card and gradients are banned here. A solid warm
                         ground is the substitute that survives that rule.
                         STILL NO HOVER STATE. Drag-over is the only thing that changes. */
                      className={`border border-dashed rounded-2xl p-8 sm:p-10 min-h-[260px] flex-1 flex items-center gap-8 cursor-pointer transition-colors ${
                        dragOver
                          ? "border-[#635BFF] bg-[#635BFF]/[0.06]"
                          : "border-[#D3CCC0] bg-[#F7F5F1]"
                      }`}
                    >
                      <div className="flex-1 min-w-[260px] text-left">
                        {/* The wordmark used to sit here, 24px tall, to keep the top-left corner
                            from being empty. It has moved to the art slot at full size — see
                            below. Two copies of one mark in a panel this small is one too many,
                            and the corner is no longer the problem it was solving. */}
                        <p className="text-slate-900 font-extrabold text-[28px] lg:text-[32px] leading-tight tracking-tight mb-1.5">
                          {projectType === "hw" ? t('create_drop_hw') : t('create_drop_lecture')}
                        </p>
                        <p className="text-[#6B655C] text-[15px] font-medium mb-5">{t('create_file_types')}</p>
                        {/* A SPAN, NOT A BUTTON. The whole zone is already role="button" with its
                            own aria-label, and nesting a real control inside an interactive element
                            is invalid — so this is the outer button's visual affordance and the
                            click simply bubbles. One tab stop, not two. */}
                        <span className="inline-flex items-center gap-2 rounded-xl bg-[#635BFF] px-5 py-3
                          text-white text-[15px] font-bold select-none">
                          <Upload className="w-4 h-4" />
                          {/* Reuses the type-picker's own subtitle rather than adding a string.
                              "Upload homework" / "Upload slides" already exist in all 15 locale
                              files and read correctly as a button; a new key would have shipped
                              English to 14 languages, because the translate endpoint is 429ing
                              and a rate-limited sync writes English AND marks it synced. */}
                          {projectType === "hw" ? t('create_hw_sub') : t('create_lecture_sub')}
                        </span>
                      </div>

                      {/* THE HAT ALONE, AT ROUGHLY 2x THE LOCKUP IT REPLACES. The word went
                          with the lockup: "Magi" set beside "Drop your homework here" put two
                          pieces of large type in one panel competing to be read first, and the
                          sentence has to win — it is the instruction. The hat carries the brand
                          without asking to be read at all, which is the whole reason it can be
                          this size.

                          240px at lg, DOWN FROM 304. At 304 the hat was setting the panel's
                          height — 304 + 40 + 40 of padding is exactly the 384px the panel came
                          out at — while the left column's heading, file types and button total
                          about 130px. That left ~87px of dead space above and below the copy,
                          which is what made it look like it was floating, and stretched the
                          faint dashed border around a large empty region. 240 brings the panel
                          to ~320, near its designed 260 minimum, and roughly halves the gap.
                          The heading moved 26 -> 32px in the same pass: at 304 the mark-to-
                          instruction ratio was 12:1, and the sentence is the only thing here
                          that has to be read.

                          Still aria-hidden. It is decoration; the zone's own aria-label names
                          what this panel is for. */}
                      <img src="/magi-icon.svg" alt="" aria-hidden="true"
                        className="hidden sm:block flex-shrink-0
                          w-[132px] h-[132px] md:w-[188px] md:h-[188px] lg:w-[240px] lg:h-[240px]" />
                      {error && <p className="text-red-500 text-[15px] mt-3">{error}</p>}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.heic"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                      />
                    </div>
                  )}

                  {phase === "analyzing" && (
                    /* THE SAME PANEL AS THE DROP ZONE, not a bare white step. Dropping a file
                       used to swap a warm bordered surface for plain white, so the dialog
                       appeared to change colour at the exact moment it changed size. Reusing
                       the zone's own ground, border, radius and min-height means the surface
                       simply STAYS PUT and its contents change — which is the transition, and
                       it costs no animation.
                       py-16 is gone: the body centres this now, so fixed padding would only
                       push it off-centre inside the floor set above. */
                    <div className="w-full border border-dashed border-[#D3CCC0] bg-[#F7F5F1] rounded-2xl
                      p-8 sm:p-10 min-h-[260px] flex-1 flex flex-col items-center justify-center text-center">
                      <p className="text-slate-900 font-extrabold text-[22px] leading-tight tracking-tight mb-1.5">
                        {projectType === "hw" ? t('create_reading_hw') : t('create_reading_lecture')}
                      </p>
                      <p className="text-[#6B655C] text-[15px] font-medium">
                        {projectType === "hw" ? t('create_finding_problems') : t('create_finding_topics')}
                      </p>
                      {/* Track sits on the warm ground, so it is a warm neutral rather than the
                          cool gray-100 that read as a hole in the surface. */}
                      <div className="w-48 h-1.5 bg-[#E4DFD7] rounded-full mt-6 overflow-hidden">
                        <div className="h-full bg-[#635BFF] rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
                      </div>
                    </div>
                  )}

                  {phase === "select" && (
                    <>
                      {/* ONE ROW: the project's name, and the only other thing you can do here.
                          "Deselect all" is gone — everything arrives selected, so the control
                          existed to undo the default, sat in the most prominent spot on the
                          step, and read as a checked box you had to think about before you
                          could read the first problem. Untick the ones you don't want instead.
                          Add-more is now a solid square rather than a text link, so it holds the
                          row against a 19px bold title; it keeps its label as an aria-label and
                          a tooltip, which is also why no new i18n key was needed. */}
                      <div className="flex items-center gap-3 mb-4">
                        <label htmlFor="project-title-select" className="sr-only">{t('create_project_title')}</label>
                        <input
                          id="project-title-select"
                          type="text"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder={t('create_project_title')}
                          className="flex-1 min-w-0 text-[19px] font-bold text-slate-900 placeholder-slate-500 border-b-2 border-slate-200 pb-2.5 focus:outline-none focus:border-[#635BFF] transition-colors"
                        />
                        <button
                          onClick={() => addMoreInputRef.current?.click()}
                          aria-label={t('create_add_more_files')}
                          title={t('create_add_more_files')}
                          className="flex-shrink-0 w-11 h-11 grid place-items-center rounded-xl
                            bg-[#635BFF] hover:bg-[#5A52E8] border-b-4 border-[#4B44CC]
                            active:translate-y-[2px] active:border-b-2 text-white transition-colors"
                        >
                          <Plus className="w-5 h-5" strokeWidth={3} />
                        </button>
                        <input
                          ref={addMoreInputRef}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.heic"
                          multiple
                          className="hidden"
                          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                        />
                      </div>

                      {/* Item list */}
                      <div className="space-y-2">
                        {extractedItems.map((item, idx) => {
                          const isSelected = selectedItems.has(idx);
                          const isHW = projectType === "hw";
                          const displayText = isHW ? item : item.title;
                          const displayDesc = isHW ? null : item.description;

                          return (
                            /* THE SELECTED STATE IS THE QUIET ONE. Everything arrives selected,
                               so a 2px indigo edge on "selected" drew that edge around every
                               card in the list — eleven saturated outlines stacked down the
                               panel, each competing with the problem text inside it, which is
                               what made the step read as loud and hard to scan. The default
                               state is now a plain white card on a hairline; the numbered chip
                               carries selection, which is the one mark that differs between the
                               two states and the one thing you click.
                               Deselected: grey fill, grey chip, muted text — clearly off. */
                            <button
                              key={idx}
                              onClick={() => toggleItem(idx)}
                              aria-pressed={isSelected}
                              className={`w-full text-left p-5 rounded-2xl border transition-colors flex items-start gap-4 ${
                                isSelected
                                  ? "border-slate-200 bg-white hover:border-slate-300"
                                  : "border-slate-200 bg-slate-100 hover:border-slate-300"
                              }`}
                            >
                              {/* The number IS the checkbox: it indexes the problem and shows
                                  selection in one mark, instead of a tiny box beside a faint
                                  grey "PROBLEM 1" that was hard to read at any size. */}
                              <span
                                className={`mt-px w-9 h-9 rounded-xl flex-shrink-0 grid place-items-center text-[15px] font-extrabold tabular-nums transition-colors ${
                                  isSelected ? "bg-[#635BFF] text-white" : "bg-slate-300 text-slate-600"
                                }`}
                                aria-hidden="true"
                              >
                                {idx + 1}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div
                                  /* 18px. A problem statement is the thing you are being asked
                                     to read and decide on, and it was set smaller than the
                                     dialog's own title while being four times as long — with
                                     inline LaTeX inside it, which renders optically smaller
                                     still, hence the 1.05em on the katex spans. Weight stays at
                                     semibold: size does the work, and bold at this length is
                                     another kind of shouting. Deselected drops to slate-500 —
                                     clearly off, still readable, no washed-out grey. */
                                  className={`leading-relaxed line-clamp-4 [&_.katex]:text-[1.05em] ${
                                    isHW
                                      ? "text-[18px] font-semibold"
                                      : "text-[18px] font-bold"
                                  } ${isSelected ? "text-slate-900" : "text-slate-500"}`}
                                  dangerouslySetInnerHTML={{ __html: renderLatex(displayText) }}
                                />
                                {displayDesc && (
                                  <p
                                    className={`text-[16px] font-medium mt-1.5 leading-relaxed line-clamp-2 [&_.katex]:text-[1.05em] ${
                                      isSelected ? "text-slate-700" : "text-slate-500"
                                    }`}
                                    dangerouslySetInnerHTML={{ __html: renderLatex(displayDesc) }}
                                  />
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Self Study: Topic slots */}
              {projectType === "self_study" && (
                <div>
                  {/* The list SCROLLS past four rows rather than growing the dialog.
                      The modal is vertically centred, so every added row used to push the
                      whole thing taller AND shift it up half a row — at ten slots it grew
                      past the viewport. A capped, scrolling list keeps the dialog a fixed
                      size from the fourth row on, so the buttons stay where you left them.
                      max-height is 4 rows plus a sliver of the fifth, which is what makes it
                      read as scrollable rather than clipped. */}
                  <div
                    ref={slotsScrollRef}
                    className="space-y-2.5 max-h-[268px] overflow-y-auto -mr-1 pr-1"
                    /* Once you add a second topic the list RESERVES its full height and
                       never changes size again. The dialog is vertically centred, so each
                       row it grew by moved the whole thing up half a row — four jumps on the
                       way to five topics, with the field under your cursor sliding out from
                       under it every time. Reserving on the first add trades one deliberate
                       expansion, at the moment you opt into a multi-video project, for a
                       panel that then stays put. Opening at the natural single-row height
                       keeps the common case (one topic) from looking half-empty. */
                    style={videoSlots.length > 1 ? { minHeight: 268 } : undefined}
                  >
                  {videoSlots.map((slot, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <label htmlFor={`topic-slot-${idx}`} className="sr-only">{t('create_video_topic_placeholder', { n: idx + 1 })}</label>
                      <input
                        id={`topic-slot-${idx}`}
                        type="text"
                        value={slot}
                        onChange={(e) => updateSlot(idx, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && idx === videoSlots.length - 1 && videoSlots.length < MAX_SELF_STUDY_SLOTS) {
                            e.preventDefault();
                            addSlot();
                          }
                        }}
                        placeholder={t('create_video_topic_placeholder', { n: idx + 1 })}
                        className="flex-1 px-3.5 py-3 border border-gray-200 rounded-lg text-[16px] font-semibold text-gray-900 placeholder-gray-500 placeholder:font-medium focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/30 transition-colors"
                      />
                      {videoSlots.length > 1 && (
                        <button
                          onClick={() => removeSlot(idx)}
                          aria-label={`Remove topic ${idx + 1}`}
                          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  </div>

                  {videoSlots.length < MAX_SELF_STUDY_SLOTS && (
                    <button
                      onClick={addSlot}
                      className="flex items-center gap-1.5 text-[15px] text-indigo-600 font-bold hover:text-indigo-700 transition-colors mt-1.5"
                    >
                      <Plus className="w-4 h-4" />
                      {t('create_add_video')}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — ONLY WHEN IT HAS SOMETHING IN IT. The button inside is gated on
            video / self_study / phase==="select", none of which hold while you are being asked
            to drop a file. So on the upload screen this rendered as a top border over 32px of
            nothing: the dialog ended in an empty ruled strip, with no call to action anywhere,
            which is most of why it read as unfinished rather than merely plain.
            `error` stays in the condition because the footer holds id="create-error", which the
            title input points at with aria-describedby. */}
        {step === "create" && phase !== "analyzing" &&
         (projectType === "video" || projectType === "self_study" || phase === "select" || error) && (
          <div className={`px-6 py-4 flex-shrink-0 border-t ${
            docked ? "border-black/10" : "border-gray-100"
          }`}>
            {error && (
              <p id="create-error" role="alert" className="text-red-500 text-sm text-center mb-3">{error}</p>
            )}
            {(projectType === "video" || projectType === "self_study" || phase === "select") && (
              <button
                onClick={handleCreate}
                disabled={!canCreate() || creating}
                className="w-full py-4 bg-[#635BFF] hover:bg-[#5A52E8] border-b-4 border-[#4B44CC]
                  active:translate-y-[2px] active:border-b-2
                  disabled:bg-slate-200 disabled:text-slate-400 disabled:border-slate-300
                  text-white text-[17px] font-extrabold rounded-2xl transition-all
                  flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    {t('create_starting')}
                  </>
                ) : projectType === "video" ? (
                  t('create_generate_video')
                ) : projectType === "self_study" ? (
                  t('create_create_project', { n: videoSlots.filter((s) => s.trim()).length })
                ) : (
                  t('create_generate_n', { n: selectedItems.size })
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
    {showSubscriptionModal && (
      <SubscriptionModal
        onClose={() => setShowSubscriptionModal(false)}
        user={user}
      />
    )}
    </>
  );
};

export default CreateProjectModal;
